import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { TrendingUp, TrendingDown, Wallet, Plus, Trash2, Receipt } from "lucide-react";

export default function Finance() {
  const [sum, setSum] = useState(null);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [cat, setCat] = useState("Genel");

  const load = () => api.get("/finance/summary").then((r) => setSum(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addExpense = async () => {
    if (!title.trim() || !amount) return toast.error("Başlık ve tutar girin");
    try { await api.post("/expenses", { title: title.trim(), amount: Number(amount), category: cat }); setTitle(""); setAmount(""); toast.success("Gider eklendi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const delExpense = async (id) => { try { await api.delete(`/expenses/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!sum) return <div className="text-zinc-500">Yükleniyor...</div>;

  const chartData = Object.entries(sum.daily || {}).sort().slice(-7).map(([d, v]) => ({ gun: d.slice(5), ciro: v }));

  const cards = [
    { label: "Toplam Ciro", value: sum.income, icon: TrendingUp, cls: "text-emerald-400" },
    { label: "Toplam Gider", value: sum.expenseTotal, icon: TrendingDown, cls: "text-rose-400" },
    { label: "Net Kâr", value: sum.net, icon: Wallet, cls: sum.net >= 0 ? "text-[#FFE599]" : "text-rose-400" },
    { label: "Tamamlanan Sipariş", value: sum.orderCount, icon: Receipt, cls: "text-blue-400", raw: true },
  ];

  return (
    <div>
      <h1 className="font-serif text-3xl sm:text-4xl text-white mb-1">Gelir - Gider Takibi</h1>
      <p className="text-zinc-400 text-sm mb-6">Ciro, giderler ve net kâr durumunuz</p>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="card-lux p-5" data-testid={`fin-card-${c.label}`}>
              <div className="flex items-center justify-between"><span className="text-xs font-mono uppercase tracking-widest text-zinc-400">{c.label}</span><Icon size={18} className={c.cls} /></div>
              <div className={`font-mono text-2xl sm:text-3xl mt-3 ${c.cls}`}>{c.raw ? c.value : `₺${Number(c.value).toFixed(2)}`}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[["Nakit Tahsilat", sum.cashTotal, "text-emerald-400"], ["Kart / POS", sum.cardTotal, "text-blue-400"], ["Açık Veresiye", sum.veresiyeOpen, "text-amber-400"], ["Masa Cirosu", sum.tableIncome, "text-[#FFE599]"]].map(([l, v, cls]) => (
          <div key={l} className="card-lux p-4" data-testid={`fin-bd-${l}`}>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-400">{l}</div>
            <div className={`font-mono text-xl mt-2 ${cls}`}>₺{Number(v || 0).toFixed(2)}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-5">
          <h2 className="font-serif text-xl text-white mb-4">Son 7 Gün Ciro</h2>
          {chartData.length === 0 ? <p className="text-zinc-500 text-sm py-10 text-center">Henüz tamamlanan sipariş yok</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData}>
                <XAxis dataKey="gun" stroke="#71717A" fontSize={12} />
                <YAxis stroke="#71717A" fontSize={12} />
                <Tooltip contentStyle={{ background: "#18181C", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, color: "#fff" }} formatter={(v) => [`₺${v}`, "Ciro"]} />
                <Bar dataKey="ciro" fill="#D4AF37" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card-lux p-5">
          <h2 className="font-serif text-xl text-white mb-4">Gider Ekle</h2>
          <div className="space-y-3">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Gider açıklaması (örn: Ekmek tedariki)" className="lux-input" data-testid="expense-title" />
            <div className="flex gap-3">
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Tutar ₺" className="lux-input flex-1" data-testid="expense-amount" />
              <select value={cat} onChange={(e) => setCat(e.target.value)} className="lux-input w-40" data-testid="expense-cat">
                {["Genel", "Malzeme", "Personel", "Kira", "Fatura", "Diğer"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <button onClick={addExpense} className="btn-gold w-full py-3 rounded-xl flex items-center justify-center gap-2" data-testid="add-expense-btn"><Plus size={18} /> Gider Ekle</button>
          </div>
          <div className="mt-5 space-y-2 max-h-52 overflow-y-auto no-scrollbar">
            {(sum.expenses || []).map((e) => (
              <div key={e.id} className="flex items-center justify-between bg-[#18181C] rounded-xl px-4 py-2.5" data-testid={`expense-${e.id}`}>
                <div><div className="text-sm text-white">{e.title}</div><div className="text-[10px] font-mono text-zinc-500 uppercase">{e.category}</div></div>
                <div className="flex items-center gap-3"><span className="font-mono text-rose-400 text-sm">-₺{e.amount.toFixed(2)}</span><button onClick={() => delExpense(e.id)} className="text-zinc-500 hover:text-rose-400" data-testid={`del-expense-${e.id}`}><Trash2 size={15} /></button></div>
              </div>
            ))}
            {(sum.expenses || []).length === 0 && <p className="text-zinc-600 text-sm text-center py-4">Henüz gider yok</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
